import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { Loader, LoaderWrapper } from '@src/components/Loader';
import { useBalances } from '@src/hooks/useBalances';
import type { Balance, BalancesFilter } from '@src/types';
import {
  BlotterDensity,
  BlotterTable,
  Checkbox,
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  IndicatorBadge,
  InlineFormattedNumber,
  LoaderSizes,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useBlotterTable,
  useConstant,
  useMixpanel,
  useObservable,
  useObservableValue,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useSecurityDefaultIncrement,
  type BuyingPower,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import type { ColDef, GridOptions, ICellRendererParams, RowClassParams } from 'ag-grid-enterprise';
import type Big from 'big.js';
import { compact, flatten, uniq, values } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { ReplaySubject } from 'rxjs';
import { useTheme } from 'styled-components';
import { BodyText, MarketAccountCheckbox, MarketsWrapper } from '../styles';
import type {
  InnerMarketAccountSelectionListProps,
  MarketAccountObject,
  MarketAccountSelectionGridProps,
  MarketAccountSelectionProps,
} from '../types';
import { getBuyingPowerSum, getMarketAccountBalance } from '../utils';
import type { MaoRow } from './MaoRow';
import {
  getUnifiedLiquidityColumnGroup,
  getUnifiedLiquidityColumnId,
  useColumns,
  type UnifiedLiquidityColumn,
} from './useColumns';

const MarketTypeEnum = {
  Exchanges: 'Exchanges',
  Dealers: 'Dealers',
  Markets: 'MARKETS',
} as const;
type MarketTypeEnum = (typeof MarketTypeEnum)[keyof typeof MarketTypeEnum];
export interface SelectionGridProps
  extends MarketAccountSelectionProps, InnerMarketAccountSelectionListProps, MarketAccountSelectionGridProps {}

export const SelectionGrid: React.FC<SelectionGridProps> = props => {
  const {
    baseBuyingPower,
    quoteBuyingPower,

    security,
    exchangeChecked,
    exchangeIndeterminate,
    handleExchangeCheckbox,
    dealerChecked,
    dealerIndeterminate,
    handleDealerCheckbox,
    exchangeMarketObjGroups,
    dealerMarketObjGroups,
    handleMarketAccountChange,
    handleMarketChange,
    onChangeMarketAccounts,
    onChangeMarkets,
    selectedMarketAccounts,
    selectedMarkets,
    selectedExchangeMarkets,
    selectedDealerMarkets,
    selectableDealerMarketAccountObjs,
    selectableExchangeMarketAccountObjs,
    unifiedLiquidityTokens,
    selectedUnifiedLiquidityMarkets,
    onChangeUnifiedLiquidityMarkets,
  } = props;
  const mixpanel = useMixpanel();
  const { securitiesBySymbol } = useSecuritiesContext();

  const baseCurrency = security?.BaseCurrency ?? '';
  const quoteCurrency = security?.QuoteCurrency ?? '';
  const baseCurrencyIncrement = useSecurityDefaultIncrement({ security, isCcy: false });
  const quoteCurrencyIncrement = useSecurityDefaultIncrement({ security, isCcy: true });

  const theme = useTheme();
  const unifiedLiquiditySymbols = useMemo(
    () => getUnifiedLiquiditySymbols(security, unifiedLiquidityTokens),
    [security, unifiedLiquidityTokens]
  );
  const balanceDeltaFilter: BalancesFilter = useMemo(() => {
    return {
      Currencies: [baseCurrency, quoteCurrency],
      MarketAccounts: compact([
        ...flatten(values(exchangeMarketObjGroups)).map(mktObject => mktObject.marketAccount?.Name),
        ...flatten(values(dealerMarketObjGroups)).map(mktObject => mktObject.marketAccount?.Name),
      ]),
    };
  }, [baseCurrency, quoteCurrency, exchangeMarketObjGroups, dealerMarketObjGroups]);

  const { balancesByMarketAccountIDCurrencyObs } = useBalances({
    filter: balanceDeltaFilter,
    tag: 'SelectionGrid',
    openStreamWithoutFilter: false,
  });
  const balancesByMarketAccountIDCurrency = useObservableValue(
    () => balancesByMarketAccountIDCurrencyObs,
    [balancesByMarketAccountIDCurrencyObs]
  );

  const unifiedLiquidityMarketAccountObjects = useMemo(
    () =>
      onChangeUnifiedLiquidityMarkets
        ? [...selectableExchangeMarketAccountObjs, ...selectableDealerMarketAccountObjs].filter(
            mao =>
              mao.marketAccount &&
              unifiedLiquiditySymbols?.some(token => {
                const tokenSecurity = securitiesBySymbol.get(token.Symbol);
                return token.Market === mao.market.Name && !!tokenSecurity?.QuoteCurrency;
              })
          )
        : [],
    [
      onChangeUnifiedLiquidityMarkets,
      selectableExchangeMarketAccountObjs,
      selectableDealerMarketAccountObjs,
      unifiedLiquiditySymbols,
      securitiesBySymbol,
    ]
  );

  const eligibleUnifiedLiquiditySymbols = useMemo(() => {
    if (!onChangeUnifiedLiquidityMarkets || !unifiedLiquiditySymbols?.length) {
      return undefined;
    }

    const eligibleMarkets = new Set(unifiedLiquidityMarketAccountObjects.map(mao => mao.market.Name));
    return unifiedLiquiditySymbols.filter(token => eligibleMarkets.has(token.Market));
  }, [onChangeUnifiedLiquidityMarkets, unifiedLiquiditySymbols, unifiedLiquidityMarketAccountObjects]);

  const { columns, unifiedLiquidityColumns } = useColumns({
    baseCurrency,
    quoteCurrency,
    unifiedLiquidityTokens: eligibleUnifiedLiquiditySymbols,
    securitiesBySymbol,
    selectedUnifiedLiquidityMarkets,
    onChangeUnifiedLiquidityMarkets,
  });

  const isUnifiedLiquidityMode = !!onChangeUnifiedLiquidityMarkets && unifiedLiquidityColumns.length > 0;

  const selectedCombined = useMemo(
    () => selectedExchangeMarkets.concat(selectedDealerMarkets),
    [selectedExchangeMarkets, selectedDealerMarkets]
  );

  const getMarketAccountObjectsSelectionState = useCallback(
    (maos: MarketAccountObject[]) => {
      const selectedCount = maos.filter(mao => selectedCombined.includes(mao.name)).length;
      return {
        checked: maos.length > 0 && selectedCount === maos.length,
        indeterminate: selectedCount > 0 && selectedCount !== maos.length,
        selectedCount,
        selectableCount: maos.length,
      };
    },
    [selectedCombined]
  );

  const getMarketAccountObjectsForGroup = useCallback(
    (params: ICellRendererParams<MaoRow>) =>
      compact(params.node.allLeafChildren?.map(childNode => childNode.data?.mao)),
    []
  );

  const handleMarketAccountObjectsSelection = useCallback(
    (maos: MarketAccountObject[], selected: boolean) => {
      const marketAccountNames = maos.flatMap(mao => (mao.marketAccount ? [mao.name] : []));
      const marketNames = maos.flatMap(mao => (mao.marketAccount ? [] : [mao.name]));

      if (selected) {
        onChangeMarketAccounts(uniq([...selectedMarketAccounts, ...marketAccountNames]));
        onChangeMarkets?.(uniq([...(selectedMarkets ?? []), ...marketNames]));
        return;
      }

      const marketAccountNamesToRemove = new Set(marketAccountNames);
      const marketNamesToRemove = new Set(marketNames);
      onChangeMarketAccounts(
        selectedMarketAccounts.filter(marketAccount => !marketAccountNamesToRemove.has(marketAccount))
      );
      onChangeMarkets?.((selectedMarkets ?? []).filter(market => !marketNamesToRemove.has(market)));
    },
    [onChangeMarketAccounts, onChangeMarkets, selectedMarketAccounts, selectedMarkets]
  );

  const innerRenderer = useCallback(
    (params: ICellRendererParams<MaoRow>) => {
      if (isUnifiedLiquidityMode && !params.node.data) {
        const maos = getMarketAccountObjectsForGroup(params);
        const marketAccountObjectsState = getMarketAccountObjectsSelectionState(maos);
        return (
          <SectionHeader
            label={`${params.value ?? ''}`}
            checked={marketAccountObjectsState.checked}
            handleCheckbox={checked => handleMarketAccountObjectsSelection(maos, checked)}
            indeterminate={marketAccountObjectsState.indeterminate}
            selectedCount={marketAccountObjectsState.selectedCount}
            selectableCount={marketAccountObjectsState.selectableCount}
          />
        );
      }

      if (params.node.level === 0) {
        if (params.value === MarketTypeEnum.Dealers) {
          return (
            <SectionHeader
              label={params.value}
              checked={dealerChecked}
              handleCheckbox={checked => {
                handleDealerCheckbox(checked);
              }}
              indeterminate={dealerIndeterminate}
              selectedCount={selectedDealerMarkets.length}
              selectableCount={selectableDealerMarketAccountObjs.length}
            />
          );
        } else {
          return (
            <SectionHeader
              label={params.value}
              checked={exchangeChecked}
              handleCheckbox={checked => {
                handleExchangeCheckbox(checked);
              }}
              indeterminate={exchangeIndeterminate}
              selectedCount={selectedExchangeMarkets.length}
              selectableCount={selectableExchangeMarketAccountObjs.length}
            />
          );
        }
      } else {
        if (params.value && params.node.data?.mao) {
          const mao = params.node.data.mao;
          const isSelected = selectedCombined.includes(mao.name);
          return (
            <MarketAccountCheckbox>
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                    [MixpanelEventProperty.Source]: MixpanelEventSource.MarketsPanel,
                  });

                  mao.isSelected = isSelected;
                  mao.marketAccount ? handleMarketAccountChange(mao) : handleMarketChange(mao);
                }}
              >
                <BodyText selected={isSelected}>{mao.label}</BodyText>
              </Checkbox>
            </MarketAccountCheckbox>
          );
        } else {
          return 'Total selected';
        }
      }
    },
    [
      dealerChecked,
      handleDealerCheckbox,
      dealerIndeterminate,
      selectedDealerMarkets.length,
      selectableDealerMarketAccountObjs,
      exchangeChecked,
      handleExchangeCheckbox,
      exchangeIndeterminate,
      selectedExchangeMarkets.length,
      selectableExchangeMarketAccountObjs,
      selectedCombined,
      isUnifiedLiquidityMode,
      mixpanel,
      handleMarketAccountChange,
      handleMarketChange,
      getMarketAccountObjectsForGroup,
      getMarketAccountObjectsSelectionState,
      handleMarketAccountObjectsSelection,
    ]
  );

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: '',
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      width: isUnifiedLiquidityMode ? 260 : 200,
      field: 'name',
      cellRendererParams: {
        suppressCount: true,
      },
      cellRendererSelector: params => ({
        component: innerRenderer,
        params,
      }),
    } satisfies ColDef<MaoRow>;
  }, [innerRenderer, isUnifiedLiquidityMode]);

  const defaultColDef = useConstant({
    cellRendererParams: {
      theme,
    },
    cellRendererSelector: params => {
      if (params.node.rowPinned) {
        return { component: (params: ICellRendererParams<MaoRow>) => <>{params.value}</> };
      } else {
        return undefined;
      }
    },
    resizable: true,
    width: 120,
    sortable: false,
  } satisfies ColDef<MaoRow>);

  const getRowClass = useCallback((params: RowClassParams) => {
    const result: string[] = [];
    if (params.data?.inGroup) {
      result.push('in-group');
    }
    if (params.data?.firstInGroup) {
      result.push('first-in-group');
    }
    if (params.data?.lastInGroup) {
      result.push('last-in-group');
    }
    return result;
  }, []);

  const gridOptions = useMemo((): GridOptions => {
    return {
      maintainColumnOrder: true,
      // Selection is custom handled in this component
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
      groupDefaultExpanded: -1,
      suppressRowDrag: true,
      getRowClass,
      defaultColDef,
      autoGroupColumnDef,
    };
  }, [getRowClass, defaultColDef, autoGroupColumnDef]);

  const setPinnedTopRowData = useCallback(() => {
    const baseBp = getBuyingPowerSum(baseBuyingPower, selectedCombined);
    const quoteBp = getBuyingPowerSum(quoteBuyingPower, selectedCombined);

    return [
      {
        name: 'Total selected',
        unsettledBase: '-',
        unsettledQuote: '-',
        maxBuyBase: NumericHeaderRow({
          number: baseBp.availableBuy,
          currency: baseCurrency,
          currencyIncrement: baseCurrencyIncrement,
        }),
        maxBuyQuote: NumericHeaderRow({
          number: quoteBp.availableBuy,
          currency: quoteCurrency,
          currencyIncrement: quoteCurrencyIncrement,
        }),
        maxSellBase: NumericHeaderRow({
          number: baseBp.availableSell,
          currency: baseCurrency,
          currencyIncrement: baseCurrencyIncrement,
        }),
        maxSellQuote: NumericHeaderRow({
          number: quoteBp.availableSell,
          currency: quoteCurrency,
          currencyIncrement: quoteCurrencyIncrement,
        }),
      },
    ];
  }, [
    baseCurrency,
    quoteCurrency,
    baseCurrencyIncrement,
    quoteCurrencyIncrement,
    baseBuyingPower,
    quoteBuyingPower,
    selectedCombined,
  ]);

  const dataSubject = useConstant(new ReplaySubject<any>(1));
  const dataObservable = useObservable(() => dataSubject.asObservable(), [dataSubject]);

  const persistence = usePersistedBlotterTable(`markets-grid-${security?.Symbol}`);
  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'id',
    density: BlotterDensity.Compact,
    persistence,
    gridOptions,
    columns,
  });

  useEffect(() => {
    if (blotterTable.gridApi) {
      let items: MaoRow[] = getMaos({
        maos: exchangeMarketObjGroups,
        baseCurrency,
        baseCurrencyIncrement,
        quoteCurrency,
        quoteCurrencyIncrement,
        baseBuyingPower,
        quoteBuyingPower,
        type: MarketTypeEnum.Exchanges,
        balancesByMarketAccountIDCurrency,
        unifiedLiquidityTokens: eligibleUnifiedLiquiditySymbols,
        securitiesBySymbol,
        unifiedLiquidityColumns,
        isUnifiedLiquidityMode,
      });
      items = items.concat(
        getMaos({
          maos: dealerMarketObjGroups,
          baseCurrency,
          baseCurrencyIncrement,
          quoteCurrency,
          quoteCurrencyIncrement,
          baseBuyingPower,
          quoteBuyingPower,
          type: MarketTypeEnum.Dealers,
          balancesByMarketAccountIDCurrency,
          unifiedLiquidityTokens: eligibleUnifiedLiquiditySymbols,
          securitiesBySymbol,
          unifiedLiquidityColumns,
          isUnifiedLiquidityMode,
        })
      );
      dataSubject.next({ data: items });

      blotterTable.gridApi.setGridOption('pinnedTopRowData', setPinnedTopRowData());
    }
  }, [
    dataSubject,
    baseCurrency,
    baseCurrencyIncrement,
    quoteCurrency,
    quoteCurrencyIncrement,
    exchangeMarketObjGroups,
    dealerMarketObjGroups,
    baseBuyingPower,
    quoteBuyingPower,
    theme,
    balancesByMarketAccountIDCurrency,
    eligibleUnifiedLiquiditySymbols,
    securitiesBySymbol,
    unifiedLiquidityColumns,
    isUnifiedLiquidityMode,
    setPinnedTopRowData,
    blotterTable.gridApi,
  ]);

  if (
    security == null ||
    (Object.keys(exchangeMarketObjGroups).length === 0 && Object.keys(dealerMarketObjGroups).length === 0)
  ) {
    return (
      <LoaderWrapper>
        <Loader size={LoaderSizes.TINY} />
      </LoaderWrapper>
    );
  }

  return (
    <ErrorBoundary>
      <MarketsWrapper>
        <BlotterTable {...blotterTable} />
      </MarketsWrapper>
    </ErrorBoundary>
  );
};

function getUnifiedLiquiditySymbols(
  security: Security | undefined,
  unifiedLiquidityTokens: UnifiedLiquidityToken[] | undefined
): UnifiedLiquidityToken[] | undefined {
  if (!unifiedLiquidityTokens?.length) {
    return undefined;
  }

  const primarySymbols =
    security?.Markets?.map(market => ({
      Market: market,
      Symbol: security.Symbol,
    })) ?? [];

  return [...primarySymbols, ...unifiedLiquidityTokens];
}

interface GetMaosParams {
  maos: { [key: string]: MarketAccountObject[] };
  baseCurrency: string;
  baseCurrencyIncrement?: string;
  quoteCurrency: string;
  quoteCurrencyIncrement?: string;
  baseBuyingPower: Map<string, BuyingPower> | undefined;
  quoteBuyingPower: Map<string, BuyingPower> | undefined;
  type: MarketTypeEnum;
  balancesByMarketAccountIDCurrency: Map<number, Map<string, Balance>> | undefined;
  unifiedLiquidityTokens: UnifiedLiquidityToken[] | undefined;
  securitiesBySymbol: Map<string, Security>;
  unifiedLiquidityColumns: UnifiedLiquidityColumn[];
  isUnifiedLiquidityMode: boolean;
}

function getMaos({
  maos,
  baseCurrency,
  baseCurrencyIncrement,
  quoteCurrency,
  quoteCurrencyIncrement,
  baseBuyingPower,
  quoteBuyingPower,
  type,
  balancesByMarketAccountIDCurrency,
  unifiedLiquidityTokens,
  securitiesBySymbol,
  unifiedLiquidityColumns,
  isUnifiedLiquidityMode,
}: GetMaosParams): MaoRow[] {
  const result: MaoRow[] = [];
  for (const key of Object.keys(maos)) {
    const items = maos[key];
    items.forEach((mao, index) => {
      const baseBpo = baseBuyingPower?.get(mao.name);
      const quoteBpo = quoteBuyingPower?.get(mao.name);

      const unifiedLiquidityMarketsByColumn = getUnifiedLiquidityMarketsByColumn({
        mao,
        unifiedLiquidityTokens,
        securitiesBySymbol,
        unifiedLiquidityColumns,
      });
      if (isUnifiedLiquidityMode && !unifiedLiquidityMarketsByColumn) {
        return;
      }

      const rowItem: MaoRow = {
        type: isUnifiedLiquidityMode ? MarketTypeEnum.Markets : type,
        id: mao.name,
        name: mao.label,
        market: mao.market.Name,
        marketGroup: mao.market.DisplayName || mao.market.Name,
        unsettledBase:
          type === MarketTypeEnum.Dealers
            ? getMarketAccountBalance(
                balancesByMarketAccountIDCurrency,
                mao?.marketAccount?.MarketAccountID,
                baseCurrency
              )
            : '',
        unsettledQuote:
          type === MarketTypeEnum.Dealers
            ? getMarketAccountBalance(
                balancesByMarketAccountIDCurrency,
                mao?.marketAccount?.MarketAccountID,
                quoteCurrency
              )
            : '',
        maxBuyBase: baseBpo?.AvailableBuy,
        maxBuyQuote: quoteBpo?.AvailableBuy,
        maxSellBase: baseBpo?.AvailableSell,
        maxSellQuote: quoteBpo?.AvailableSell,
        mao,
        inGroup: items.length > 1,
        firstInGroup: index === 0,
        lastInGroup: index === items.length - 1,
        baseCurrency,
        baseCurrencyIncrement,
        quoteCurrency,
        quoteCurrencyIncrement,
        unifiedLiquidityMarketsByColumn,
      };
      result.push(rowItem);
    });
  }
  return result;
}

function getUnifiedLiquidityMarketsByColumn({
  mao,
  unifiedLiquidityTokens,
  securitiesBySymbol,
  unifiedLiquidityColumns,
}: {
  mao: MarketAccountObject;
  unifiedLiquidityTokens: UnifiedLiquidityToken[] | undefined;
  securitiesBySymbol: Map<string, Security>;
  unifiedLiquidityColumns: UnifiedLiquidityColumn[];
}) {
  if (!mao.marketAccount || !unifiedLiquidityTokens?.length || !unifiedLiquidityColumns.length) {
    return undefined;
  }

  const marketAccount = mao.marketAccount;
  const availableColumnIds = new Set(unifiedLiquidityColumns.map(column => column.id));
  const selectionsByColumn = unifiedLiquidityTokens.reduce<NonNullable<MaoRow['unifiedLiquidityMarketsByColumn']>>(
    (result, token) => {
      if (token.Market !== mao.market.Name) {
        return result;
      }
      const security = securitiesBySymbol.get(token.Symbol);
      const group = getUnifiedLiquidityColumnGroup(security);
      if (!security?.QuoteCurrency || !group) {
        return result;
      }
      const columnId = getUnifiedLiquidityColumnId(group, security.QuoteCurrency);
      if (!availableColumnIds.has(columnId)) {
        return result;
      }
      return {
        ...result,
        [columnId]: [
          ...(result[columnId] ?? []),
          {
            marketAccount: marketAccount.Name,
            symbol: token.Symbol,
          },
        ],
      };
    },
    {}
  );

  return Object.keys(selectionsByColumn).length > 0 ? selectionsByColumn : undefined;
}

interface SectionHeaderProps {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  handleCheckbox: (checked: boolean) => void;
  selectedCount: number;
  selectableCount: number;
}

function SectionHeader({
  label,
  checked,
  indeterminate,
  handleCheckbox,
  selectedCount,
  selectableCount,
}: SectionHeaderProps) {
  return (
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => handleCheckbox(!checked)}>
      <BodyText selected={checked}>
        {label}
        <IndicatorBadge children={`${selectedCount} / ${selectableCount}`} />
      </BodyText>
    </Checkbox>
  );
}

interface NumericHeaderRowProps {
  number: Big;
  currency: string;
  currencyIncrement: string | undefined;
}

function NumericHeaderRow({ number, currency, currencyIncrement }: NumericHeaderRowProps) {
  return (
    <>
      {!number.eq(0) ? (
        <InlineFormattedNumber number={number} increment={currencyIncrement} currency={currency} />
      ) : (
        '-'
      )}
    </>
  );
}
